import { useToast } from "@/components/ui/use-toast";
import {
  getDateRangeSyncUrl,
  getMagicLinkUrl,
  getProviderSyncUrl,
  getCalcUrl,
} from "@/lib/api";
import { useAppStore } from "@/stores";
import { useJune } from "@/hooks/useJune";
import { DateRange } from "react-day-picker";
import { mutate as globalMutate } from "swr";

export const useAPI = () => {
  const {
    currentUser,
    isCalculating,
    isSyncing,
    setCalculating,
    setSyncing,
    scope
  } = useAppStore((state) => state);

  // For backward compatibility
  const isLoading = isSyncing || isCalculating;
  const { toast } = useToast();
  const analytics = useJune();

  const handleAPIError = async (response: Response) => {
    const { status } = response || {};

    // An error occurred
    const errorText = await response.text();
    if (status === 401) {
      toast({
        title: errorText || "Failed to renew token",
        variant: "destructive",
        description: "You may need to reauthenticate the provider.",
        // action: (
        //   <ToastAction
        //     onClick={() =>
        //       window.open(getAuthUrl(company.id, errorText), "_blank")
        //     }
        //     altText="Reauthenticate"
        //   >
        //     Reauthenticate
        //   </ToastAction>
        // ),
        duration: 20000,
      });
    } else {
      toast({
        title: "Could not sync data",
        variant: "destructive",
        description: errorText,
        // action: (
        //   <ToastAction onClick={handleClick} altText="Try again">
        //     Try again
        //   </ToastAction>
        // ),
        duration: 20000,
      });
    }

    return response;
  };

  const getError = (chunks: string[]) => {
    return chunks.find((c) => c.toLowerCase().includes("error"));
  };

  const hasError = (chunks: string[]) => {
    return chunks.some((c) => c.toLowerCase().includes("error"));
  };

  const tryFetchCatch = async (url: string) => {
    // try {
    setSyncing(true);
    return fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
      },
    })
      .then((response) => {
        // Get the readable stream from the response body
        const stream = response.body;
        toast({
          title: `Synchronization initiated`,
          description: `Syncing data. This may take up to a minute.`,
          duration: 60000,
        });
        // Get the reader from the stream
        const reader = stream?.getReader();
        const chunks: string[] = [];

        // Define a function to read each chunk
        const readChunk = () => {
          // Read a chunk from the reader
          reader
            ?.read()
            .then(({ value, done }) => {
              // Check if the stream is done
              if (done) {
                // Log a message
                if (chunks.length > 0) {
                  if (hasError(chunks)) {
                    toast({
                      title: "Could not sync data",
                      variant: "destructive",
                      description: getError(chunks),
                      duration: 10000,
                    });
                  } else {
                    // Find the sync-complete event that contains the count
                    let syncCount = 0;
                    for (const chunk of chunks) {
                      if (chunk.includes('event: sync-complete')) {
                        try {
                          // Extract the data part from the SSE message
                          const dataMatch = chunk.match(/data: ({.*})/i);
                          if (dataMatch && dataMatch[1]) {
                            const eventData = JSON.parse(dataMatch[1]);
                            if (eventData && typeof eventData.count === 'number') {
                              syncCount = eventData.count;
                              break;
                            }
                          }
                        } catch (e) {
                          console.error('Error parsing sync-complete event:', e);
                        }
                      }
                    }

                    toast({
                      title: `Synchronization completed`,
                      description: `${syncCount} deals were successfully synced.`,
                      duration: 10000,
                    });
                  }
                } else {
                  toast({
                    title: "Nothing to sync",
                    description:
                      "No deals found for the selected time period. Reconnect integrations if you believe this is an error.",
                    duration: 10000,
                  });
                }
                setSyncing(false);
                // Return from the function
                return;
              }
              // Convert the chunk value to a string

              const stringValue = new TextDecoder().decode(value, {
                stream: true,
              });
              // // Log the chunk string
              // console.log("value", chunkString);
              chunks.push(stringValue);

              // const json = JSON.parse(chunkString);
              // Read the next chunk
              readChunk();
            })
            .catch((error) => {
              // Log the error
              console.error(error);
            });
        };
        // Start reading the first chunk
        readChunk();
        return new Response();
      })
      .catch((error) => {
        // Log the error
        console.error(error);
        return new Response();
      });
    //   const reader = response?.body?.getReader();

    //   return new ReadableStream({
    //     start(controller) {
    //       return pump();
    //       function pump() {
    //         return reader.read().then(({ done, value }) => {
    //           // When no more data needs to be consumed, close the stream
    //           if (done) {
    //             controller.close();
    //             return;
    //           }
    //           // Enqueue the next data chunk into our target stream
    //           controller.enqueue(value);
    //           return pump();
    //         });
    //       }
    //     },
    //   });
    //   while (true) {
    //     const { value, done } = (await reader?.read()) || {};

    //     if (done) {
    //       console.log("The stream was already closed!");
    //       setIsLoading(false);
    //       break;
    //     } else {
    //       console.log(value);
    //     }
    //   }
    //   // setIsLoading(false);
    //   // if (!response.ok) return handleAPIError(response);
    //   return handleAPISuccess(response, scheduled);
    // } catch (error) {
    //   setIsLoading(false);
    //   return handleAPIError(
    //     new Response("An error occurred while fetching data.")
    //   );
    // }
  };

  const syncDealsByDateRange = async (
    dateRange: DateRange
  ): Promise<Response> => {
    if (!currentUser) return handleAPIError({} as Response);

    // Return a promise that resolves when sync is fully completed
    return new Promise((resolve) => {
      setSyncing(true);

      fetch(getDateRangeSyncUrl(currentUser.company_id, dateRange), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
        },
      })
        .then((response) => {
          // Get the readable stream from the response body
          const stream = response.body;
          toast({
            title: `Synchronization initiated`,
            description: `Syncing data. This may take up to a minute.`,
            duration: 60000,
          });

          // Get the reader from the stream
          const reader = stream?.getReader();
          const chunks: string[] = [];

          // Define a function to read each chunk
          const readChunk = () => {
            // Read a chunk from the reader
            reader
              ?.read()
              .then(({ value, done }) => {
                // Check if the stream is done
                if (done) {
                  // Log a message
                  if (chunks.length > 0) {
                    if (hasError(chunks)) {
                      toast({
                        title: "Could not sync data",
                        variant: "destructive",
                        description: getError(chunks),
                        duration: 10000,
                      });
                    } else {
                      // Find the sync-complete event that contains the count
                      let syncCount = 0;
                      for (const chunk of chunks) {
                        if (chunk.includes('event: sync-complete')) {
                          try {
                            // Extract the data part from the SSE message
                            const dataMatch = chunk.match(/data: ({.*})/i);
                            if (dataMatch && dataMatch[1]) {
                              const eventData = JSON.parse(dataMatch[1]);
                              if (eventData && typeof eventData.count === 'number') {
                                syncCount = eventData.count;
                                break;
                              }
                            }
                          } catch (e) {
                            console.error('Error parsing sync-complete event:', e);
                          }
                        }
                      }

                      toast({
                        title: `Synchronization completed`,
                        description: `${syncCount} deals were successfully synced.`,
                        duration: 10000,
                      });
                    }
                  } else {
                    toast({
                      title: "Nothing to sync",
                      description:
                        "No deals found for the selected time period. Reconnect integrations if you believe this is an error.",
                      duration: 10000,
                    });
                  }
                  setSyncing(false);

                  // Resolve the promise when sync is complete
                  resolve(new Response(JSON.stringify({ success: true }), {
                    status: 200,
                    headers: { 'Content-Type': 'application/json' }
                  }));
                  return;
                }

                // Convert the chunk value to a string
                const stringValue = new TextDecoder().decode(value, {
                  stream: true,
                });
                chunks.push(stringValue);

                // Read the next chunk
                readChunk();
              })
              .catch((error) => {
                // Log the error
                console.error(error);
                setSyncing(false);

                // Resolve with error
                resolve(new Response(JSON.stringify({ success: false, error: error.message }), {
                  status: 500,
                  headers: { 'Content-Type': 'application/json' }
                }));
              });
          };

          // Start reading the first chunk
          readChunk();
        })
        .catch((error) => {
          // Log the error
          console.error(error);
          setSyncing(false);

          // Resolve with error
          resolve(new Response(JSON.stringify({ success: false, error: error.message }), {
            status: 500,
            headers: { 'Content-Type': 'application/json' }
          }));
        });
    });
  };

  const syncDealsByProvider = async (
    provider: string,
    providerId?: string
  ): Promise<Response> => {
    if (!currentUser) return handleAPIError({} as Response);
    return tryFetchCatch(
      getProviderSyncUrl(provider, currentUser.company_id, providerId)
    );
  };

  const getMagicLink = async (email: string) => {
    // Create a fetch query to sign in with Magic Link
    const response = await fetch(getMagicLinkUrl(email), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
      },
    });
    if (!response.ok) {
      return handleAPIError(response);
    }
    return await response.json();
  };

  const calculateCommissions = async (dateRange: DateRange, scope?: { type: string; value: string } | null, isProjected: boolean = false, showToasts: boolean = true, analyticsContext?: { triggeredBy?: string; trigger?: string }): Promise<Response> => {
    if (!currentUser) return handleAPIError({} as Response);

    // Determine which userIds to include based on the provided scope
    let userIds: string[] | undefined = undefined;

    if (scope && scope.type === "user") {
      // If scope is a specific user, use only that user's ID
      userIds = [scope.value];
    }
    // For company scope, team scope or no scope, leave userIds undefined to include all users

    const isEphemeral = false; // DO save to db

    // Get the calculation URL path with the isProjected parameter
    const urlPath = getCalcUrl(
      currentUser,
      dateRange,
      userIds,
      isProjected,
      isEphemeral,
      true
    );
    if (!urlPath) return handleAPIError({} as Response);

    // Construct the full URL - make sure we don't have double slashes
    const apiBaseUrl = import.meta.env.VITE_API_URL.endsWith('/')
      ? import.meta.env.VITE_API_URL.slice(0, -1)
      : import.meta.env.VITE_API_URL;
    const fullUrl = `${apiBaseUrl}/${urlPath}`;

    console.log("Calling calculation API:", fullUrl);
    setCalculating(true);

    // Track the calculation in analytics
    analytics?.track("Commissions calculated", {
      dateRange,
      scopeType: scope?.type || "company",
      userCount: userIds?.length,
      calculationType: isProjected ? "projected" : "current",
      // Include any additional context provided
      ...(analyticsContext || {})
    });

    try {
      // Make the API request
      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
        },
      });

      console.log("API response status:", response.status, response.statusText);

      if (!response.ok) {
        setCalculating(false);
        return handleAPIError(response);
      }

      // Start with an initial toast notification (if enabled)
      if (showToasts) {
        toast({
          title: `${isProjected ? 'Projected' : 'Current'} calculation initiated`,
          description: `Calculating ${isProjected ? 'projected' : 'current'} commissions. This may take some time.`,
          duration: 60000,
        });
      }

      // Get the readable stream from the response body
      const stream = response.body;
      console.log("Stream available:", !!stream);

      if (!stream) {
        setCalculating(false);
        return handleAPIError(new Response("No stream available"));
      }

      // Get the reader from the stream
      const reader = stream.getReader();
      console.log("Stream reader created");

      // Track processed users
      let processedUsers = 0;
      let hasErrors = false;
      const errors: string[] = [];

      // Process the stream chunks
      const processStream = async () => {
        try {
          let continueReading = true;
          let buffer = "";

          while (continueReading) {
            const { value, done } = await reader.read();
            console.log("Stream read:", { done, hasValue: !!value });

            if (done) {
              // Process any remaining data in the buffer
              if (buffer.trim()) {
                try {
                  const data = JSON.parse(buffer);
                  console.log("Final data from buffer:", data);

                  if (data.data && Array.isArray(data.data)) {
                    processedUsers += data.data.length;
                  }
                } catch (e) {
                  console.error("Error parsing final buffer:", e);
                }
              }

              // Stream is complete
              console.log("Stream complete, processed users:", processedUsers);
              setCalculating(false);

              // Refresh the commissions data in the SWR cache
              // This will update any components using the useCommissionsData hook

              // Refresh all commissions-related cache keys
              globalMutate((key) => {
                // Match any array key where the first element is "commissions"
                if (Array.isArray(key) && key[0] === "commissions") {
                  console.log("Refreshing cache key:", key);
                  return true;
                }
                return false;
              });

              // Show final toast (if enabled)
              if (showToasts) {
                if (hasErrors) {
                  toast({
                    title: "Calculation completed with errors",
                    variant: "destructive",
                    description: errors.join("\n").substring(0, 255),
                    duration: 10000,
                  });
                } else {
                  toast({
                    title: "Calculation completed",
                    description: `Successfully processed ${processedUsers} users.`,
                    duration: 10000,
                  });
                }
              }

              continueReading = false;
              continue;
            }

            // Process this chunk
            const chunk = new TextDecoder().decode(value);
            console.log("Received chunk:", chunk.substring(0, 100) + (chunk.length > 100 ? "..." : ""));

            // Add this chunk to our buffer
            buffer += chunk;

            // Try to parse complete JSON objects from the buffer
            try {
              // The response might contain multiple JSON objects or incomplete ones
              // We'll try to parse what we have so far
              const data = JSON.parse(buffer);
              console.log("Parsed data:", data);

              // Clear the buffer since we successfully parsed it
              buffer = "";

              if (data.data && Array.isArray(data.data)) {
                // Count processed users
                processedUsers += data.data.length;

                // Update toast with progress (if enabled)
                if (showToasts && processedUsers > 0 && processedUsers % 5 === 0) {
                  toast({
                    title: "Calculation in progress",
                    description: `Processed ${processedUsers} users so far...`,
                    duration: 3000,
                  });
                }
              }

              // Check for errors in logs
              if (data.logs && Array.isArray(data.logs)) {
                const errorLogs = data.logs.filter((log: string) =>
                  log.toLowerCase().includes("error"));

                if (errorLogs.length > 0) {
                  hasErrors = true;
                  errors.push(...errorLogs);
                }
              }
            } catch (parseError) {
              // Not valid JSON or other parsing error
              console.error("Error parsing stream chunk:", parseError);
            }
          }
        } catch (error) {
          setCalculating(false);
          console.error("Stream processing error:", error);

          toast({
            title: "Calculation error",
            variant: "destructive",
            description: `Error processing calculation stream: ${error instanceof Error ? error.message : String(error)}`,
            duration: 10000,
          });
        }
      };

      // Start processing the stream
      processStream();

      return new Response();
    } catch (error) {
      setCalculating(false);
      console.error("Calculation error:", error);

      return handleAPIError(
        new Response(`Error calculating commissions: ${error instanceof Error ? error.message : String(error)}`)
      );
    }
  };

  return {
    syncDealsByDateRange,
    syncDealsByProvider,
    calculateCommissions,
    getMagicLink,
    isLoading,
    isCalculating,
    isSyncing,
    setCalculating,
  };
};
